import { createReducer } from '@reduxjs/toolkit';
import type { Draft } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';
import type { Action } from 'redux';

import type { VehicleFlag } from 'vms/features/flags/types';

import {
  LOAD_VEHICLES_SUCCESS,
  VEHICLE_META_LOAD_SUCCESS,
  VEHICLE_APPLY_UPDATE,
  IN_WORK_CHANGE_SUCCESS,
  LOAD_VEHICLE_IN_WORK_SUCCESS,
  VEHICLE_ADD_FLAG,
  VEHICLE_UPDATE_FLAG,
  VEHICLE_IN_STATUS_CREATED,
  VIEW_VEHICLE,
} from './actions';
import RecentVehicles from './RecentVehicles';
import type { Vehicle, InWork, ExtendedInWork, Status } from './types';

export interface State {
  entities: { [id: string]: Vehicle };
  vehicleInWork: InWork | null;
  meta: {
    counts: Record<Status, number>;
    sources: string[];
    buyers: string[];
  };
}

export const initialState: State = {
  entities: {},
  vehicleInWork: null,
  meta: {
    counts: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0,
      13: 0,
      14: 0,
      15: 0,
      16: 0,
      17: 0,
    },
    sources: [],
    buyers: [],
  },
};

type LoadVehiclesSuccessAction = Action<typeof LOAD_VEHICLES_SUCCESS> & {
  vehicles: Vehicle[];
};
type VehicleMetaLoadSuccessAction = Action<typeof VEHICLE_META_LOAD_SUCCESS> & {
  meta: State['meta'];
};
type VehicleApplyUpdateAction = Action<typeof VEHICLE_APPLY_UPDATE> & {
  vehicle: Vehicle;
};
type InWorkChangeSuccessAction = Action<typeof IN_WORK_CHANGE_SUCCESS> & {
  inWork: ExtendedInWork;
  finished: boolean;
  isCurrentUser: boolean;
};
type LoadVehicleInWorkSuccessAction = Action<typeof LOAD_VEHICLE_IN_WORK_SUCCESS> & {
  inWork: InWork | null;
};
type VehicleAddFlagAction = Action<typeof VEHICLE_ADD_FLAG> & { flag: VehicleFlag };
type VehicleUpdateFlagAction = Action<typeof VEHICLE_UPDATE_FLAG> & { flag: VehicleFlag };
type ViewVehicleAction = Action<typeof VIEW_VEHICLE> & { vehicle: Vehicle };
type VehicleInStatusCreatedAction = Action<typeof VEHICLE_IN_STATUS_CREATED> & {
  id: string;
  vehicleId: string;
};

const loadVehiclesSuccess = (state: Draft<State>, action: LoadVehiclesSuccessAction) => {
  state.entities = { ...state.entities, ..._.keyBy(action.vehicles, 'id') };
};

const vehiclesMetaLoadSuccess = (
  state: Draft<State>,
  action: VehicleMetaLoadSuccessAction
) => {
  state.meta = action.meta;
};

const recalcCounts = (state: Draft<State>, prev: Vehicle | undefined, next: Vehicle) => {
  if (prev && prev.status !== next.status) {
    state.meta.counts[prev.status] -= 1;
    state.meta.counts[next.status] += 1;
  }
};

const vehicleApplyUpdate = (state: Draft<State>, action: VehicleApplyUpdateAction) => {
  const { vehicle } = action;
  const old = state.entities[vehicle.id];
  state.entities = { ...state.entities, [vehicle.id]: vehicle };
  recalcCounts(state, old, vehicle);
};

const inWorkChangeSuccess = (state: Draft<State>, action: InWorkChangeSuccessAction) => {
  const { inWork, finished, isCurrentUser } = action;
  state.entities[inWork.vehicle.id] = inWork.vehicle;
  if (isCurrentUser) {
    state.vehicleInWork = finished ? null : { ...inWork, vehicle: inWork.vehicle.id };
  }
};

const loadVehicleInWorkSuccess = (
  state: Draft<State>,
  action: LoadVehicleInWorkSuccessAction
) => {
  state.vehicleInWork = action.inWork;
};

const vehicleAddFlag = (state: Draft<State>, action: VehicleAddFlagAction) => {
  state.entities[action.flag.vehicle].flags.push(action.flag);
};

const vehicleUpdateFlag = (state: Draft<State>, action: VehicleUpdateFlagAction) => {
  const { flag } = action;
  const flags = state.entities[flag.vehicle].flags;
  for (let i = 0; i < flags.length; i++) {
    if (flags[i].id === flag.id) {
      flags[i] = flag;
    }
  }
};

const viewVehicle = (_state: Draft<State>, action: ViewVehicleAction) => {
  RecentVehicles.saveToHistory(action.vehicle);
};

const vehicleInStatusCreated = (
  state: Draft<State>,
  action: VehicleInStatusCreatedAction
) => {
  const { id, vehicleId } = action;
  const vehicle = state.entities[vehicleId];
  const existing = vehicle.vehicle_in_statuses.filter((x) => x.id === id);
  if (!existing.length) {
    vehicle.vehicle_in_statuses.push({ id, vehicle_status: vehicle.status });
  }
};

export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(LOAD_VEHICLES_SUCCESS, loadVehiclesSuccess);
  builder.addCase(VEHICLE_META_LOAD_SUCCESS, vehiclesMetaLoadSuccess);
  builder.addCase(VEHICLE_APPLY_UPDATE, vehicleApplyUpdate);
  builder.addCase(IN_WORK_CHANGE_SUCCESS, inWorkChangeSuccess);
  builder.addCase(LOAD_VEHICLE_IN_WORK_SUCCESS, loadVehicleInWorkSuccess);
  builder.addCase(VEHICLE_ADD_FLAG, vehicleAddFlag);
  builder.addCase(VEHICLE_UPDATE_FLAG, vehicleUpdateFlag);
  builder.addCase(VIEW_VEHICLE, viewVehicle);
  builder.addCase(VEHICLE_IN_STATUS_CREATED, vehicleInStatusCreated);
});
